import React, { useState, useEffect, useRef } from 'react';
import ModalPage from 'components/explore/layout/ModalPage';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Tooltip,
  Button,
  TextField,
  IconButton,
  Collapse,
  CircularProgress,
  MenuItem,
  Select,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import {
  Paging,
  Column,
  HeaderFilter,
  SearchPanel,
} from 'devextreme-react/data-grid';
import DataGrid from 'components/devExtreme/DataGrid';
import { Constants } from 'utilities/constants';
import {
  fetchAssumptionGroups,
  createAssumptionGroup,
  deleteAssumptionGroup,
  setConfirmationModalState,
} from 'store/actions';
import { getWithToken } from 'api/network';

const pageSize = 10;

function GroupManagerGrid({
  classes,
  user,
  groups,
  selectedModuleId,
  fetchAssumptionGroups,
  createAssumptionGroup,
  deleteAssumptionGroup,
  modalOpen,
  shouldCollapseGrid,
  groupsAreLoading,
  onClose,
  setConfirmationModalState,
}) {
  // == CONSTANTS =============================================

  const gridRef = useRef(null);

  // == STATE =================================================

  const [groupNameInput, setGroupNameInput] = useState('');
  const [groupNameError, setGroupNameError] = useState('');
  const [groupDescriptionInput, setGroupDescriptionInput] = useState('');
  const [saveGroupExpanded, setSaveGroupExpanded] = useState(false);
  const [gridExpanded, setGridExpanded] = useState(true);
  const [assumptionGroupType, setAssumptionGroupType] = useState(null);
  const [possibleAssumptionGroupTypes, setPossibleAssumptionGroupTypes] = useState([]);

  // == EFFECTS ===============================================

  async function fetchAssumptionGroupTypes() {
    const url = `/api/assumptions/assumptionGroupTypes`
    const response = await getWithToken(url)
    const possibleValues = await response.json()
    setPossibleAssumptionGroupTypes(possibleValues)
  }

  useEffect(() => {
    if (modalOpen) {
      fetchAssumptionGroups(selectedModuleId);

      if (shouldCollapseGrid) {
        setSaveGroupExpanded(true);
        setGridExpanded(false);
      } else {
        setGridExpanded(true);
      }

      fetchAssumptionGroupTypes()
    }
  }, [modalOpen, shouldCollapseGrid]);

  // == EVENT HANDLERS =======================================

  const setDefaults = () => {
    setGroupNameInput('');
    setGroupDescriptionInput('');
  };

  const handleGroupDelete = (e) => {
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => {
        const { groupId } = e.data;
        closeConfirmationModal();
        deleteAssumptionGroup(groupId);
      },
      handleCancellation: () => {
        closeConfirmationModal();
      },
      titleText: 'Confirm',
      contentText: `Are you sure you want to delete this Group?`,
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  };

  const handleGroupNameInputChange = (e) => {
    if (groupNameError?.length) {
      setGroupNameError('');
    }

    setGroupNameInput(e.target.value);
  };

  const handleGroupDescriptionInputChange = (e) => {
    setGroupDescriptionInput(e.target.value);
  };

  const onAssumptionGroupTypeChanged = (value) => {
    setAssumptionGroupType(value)
  }

  const onCreateNewGroupClick = () => {
    if (groupNameInput === '') {
      setGroupNameError('Name is required');
      return;
    }

    createAssumptionGroup({
      moduleId: selectedModuleId,
      name: groupNameInput,
      description: groupDescriptionInput,
      assumptionGroupType: assumptionGroupType,
    });

    setDefaults();
    onClose();
  };

  const groupGridDataSource = () => {
    return groups?.length
      ? groups.map((group) => {
          if (!group.deleted) {
            return {
              ...group,
              canDelete: user.roles.administrator,
            };
          }
        })
      : [];
  };

  // == COMPONENTS ==========================================

  const deleteColumnRender = (e) => {
    if (e.data.canDelete === false) return null;
    return (
      <Tooltip title={'Delete'} placement='bottom'>
        <IconButton
          style={{
            padding: 2,
            margin: -2,
          }}
          onClick={() => handleGroupDelete(e)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <ModalPage
      maxWidth='md'
      title='Group Manager'
      open={modalOpen}
      onClose={onClose}
    >
      <div style={{ display: 'flex' }}>
        <IconButton
          className={classes.expandButton}
          style={{
            transform: `rotate(${saveGroupExpanded ? '0' : '-90'}deg)`,
          }}
          onClick={() => setSaveGroupExpanded(!saveGroupExpanded)}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant='subtitle1'>Create New Group</Typography>
      </div>
      <Collapse in={saveGroupExpanded}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #e0e0e0',
            padding: '8px 8px 4px',
          }}
        >
          <TextField
            autoFocus={shouldCollapseGrid}
            label='Name'
            placeholder='Enter a name...'
            onChange={handleGroupNameInputChange}
            value={groupNameInput}
            style={{ width: 346, height: 68, marginRight: 16 }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              maxLength: 50,
            }}
            error={groupNameError?.length > 0}
            helperText={groupNameError}
          />
          <TextField
            label='Description'
            placeholder='Enter a description...'
            onChange={handleGroupDescriptionInputChange}
            value={groupDescriptionInput}
            style={{ width: 346, height: 68, marginRight: 16 }}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              maxLength: 100,
            }}
          />
          <Select
            name='assumptionGroupType'
            value={assumptionGroupType}
            onChange={(e) => onAssumptionGroupTypeChanged(e.target.value)}
          >
            <MenuItem value={null}>--None</MenuItem>
            {possibleAssumptionGroupTypes.map(enumValue => {
              return (
                <MenuItem value={enumValue.id}>{enumValue.display}</MenuItem>
              )
            })}
          </Select>
          <div style={{ flexGrow: 1, textAlign: 'end' }}>
            <Button
              variant='outlined'
              color='primary'
              onClick={onCreateNewGroupClick}
              style={{ margin: '0 8px' }}
            >
              Create
            </Button>
          </div>
        </div>
      </Collapse>
      <div className={classes.spacer} />
      <div style={{ display: 'flex' }}>
        <IconButton
          className={classes.expandButton}
          style={{
            transform: `rotate(${gridExpanded ? '0' : '-90'}deg)`,
            zIndex: 1,
          }}
          onClick={() => setGridExpanded(!gridExpanded)}
        >
          <ExpandMoreIcon />
        </IconButton>
        <Typography variant='subtitle1' style={{ zIndex: 1 }}>
          Saved Groups
        </Typography>
        {gridExpanded && (
          <IconButton
            className={classes.refreshButton}
            onClick={() => fetchAssumptionGroups(selectedModuleId)}
            style={{ zIndex: 1 }}
          >
            <RefreshIcon />
          </IconButton>
        )}
      </div>
      <Collapse in={gridExpanded}>
        {groupsAreLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <DataGrid
              id='groupSavedFilterGrid'
              ref={gridRef}
              keyExpr={'groupId'}
              persistState
              dataSource={groupGridDataSource()}
              height={466}
              style={{ marginTop: -40 }}
            >
              <Column dataField={'name'} />
              <Column dataField={'description'} />
              <Column dataField={'createdBy'} />
              <Column
                dataField={'createdOn'}
                dataType={'date'}
                format={'MM/dd/yyyy'}
              />
              <Column
                dataField={'isActive'}
                dataType={'boolean'}
              />
              <Column dataField={'assumptionGroupType'} />
              <Column
                dataField={'publishedOn'}
                dataType={'date'}
                format={'MM/dd/yyyy'}
              />
              <Column dataField={'updatedBy'} />
              <Column
                dataField={'updatedOn'}
                dataType={'date'}
                format={'MM/dd/yyyy'}
              />
              <Column
                cellRender={deleteColumnRender}
                alignment='center'
                width={48}
              />
              <Paging defaultPageSize={pageSize} />
              <HeaderFilter visible={true} allowSearch={true} hideSelectAllOnSearch={false} />
              <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
            </DataGrid>
          </>
        )}
      </Collapse>
    </ModalPage>
  );
}

const styles = (theme) => ({
  locationToggleContainer: {
    margin: '-44px 0px 1px 4px',
    display: 'flex',
    alignItems: 'center',
  },
  tooltipIcon: {
    zIndex: 1,
    fontSize: 16,
    marginLeft: 4,
  },
  footnote: {
    paddingTop: 10,
  },
  spacer: {
    height: 16,
  },
  savedFilterButtons: {
    fontSize: 12,
  },
  btnApply: {
    marginRight: 10,
    padding: 2,
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: `5px`,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      cursor: `pointer`,
    },
  },
  refreshButton: {
    padding: 2,
    marginLeft: 6,
  },
  queryBuilder: {
    '& div.e-rule-field': {
      display: 'flex',
    },
    '& div.e-rule-filter': {
      width: '400px !important',
    },
    '& div.e-rule-value-delete': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  expandButton: {
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    padding: 2,
    marginRight: 6,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 426,
  },
});

function mapState({ assumptionModulesState, assumptionGroupsState, appState }) {
  return {
    user: appState.user,
    selectedModuleId: assumptionModulesState.selectedModuleId,
    groups: assumptionGroupsState.groups,
    groupsAreLoading: assumptionGroupsState.groupsAreLoading,
  };
}

const mapDispatch = {
  fetchAssumptionGroups,
  createAssumptionGroup,
  deleteAssumptionGroup,
  setConfirmationModalState,
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(GroupManagerGrid);
